import { CrtSection, CrtField, CrtNotice } from './crtKit';

/**
 * FANUC ADAPTER page — what the real FOCAS adapter reports over the wire for
 * this machine: the engine slice (§6 engine) and the resolved tool-life
 * addresses (§6 tlm_*). Fed by the shared useFocasAdapter poll (via props) so
 * the DGNOS page can share the same fetch.
 */
export default function AdapterScreen({ adapter }) {
  const { values, engine, reachable } = adapter;

  if (!reachable) {
    return (
      <CrtNotice tone="warn">
        focas-hero is not reachable. Expected in local dev without the adapter; on prod the adapter serves
        this machine. See DGNOS for detail.
      </CrtNotice>
    );
  }

  const macros = values?.tlm_macros || {};
  const pmc = values?.tlm_pmc || {};
  const hasSinks = Object.keys(macros).length > 0 || Object.keys(pmc).length > 0;

  return (
    <div className="text-sm">
      <CrtSection title="Engine (adapter)">
        {engine ? (
          <div className="fanuc-cell p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            <CrtField label="Execution" value={engine.execution ?? '—'} />
            <CrtField label="Cycle" value={`${Math.round(engine.cycle_progress ?? 0)}%`} />
            <CrtField label="Part count" value={engine.part_count ?? '—'} />
            <CrtField label="Program" value={engine.program || '—'} />
            <CrtField label="Faulted" value={engine.faulted ? 'YES' : 'no'} danger={engine.faulted} />
            <CrtField label="Alarm" value={engine.alarm_code || '—'} danger={!!engine.alarm_code} />
          </div>
        ) : (
          <div className="fanuc-label px-1 py-2">Waiting for adapter data…</div>
        )}
      </CrtSection>

      <CrtSection title="Tool-life — resolved addresses (from adapter)">
        <div className="fanuc-cell overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="fanuc-label text-[10px] uppercase tracking-wide">
                <th className="text-left px-3 py-2">Address</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-right px-3 py-2">Count</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(macros).map(([macro, count]) => (
                <tr key={`m-${macro}`} style={{ borderTop: '1px solid color-mix(in srgb, var(--fx-dim) 25%, transparent)' }}>
                  <td className="px-3 py-1.5 font-mono fanuc-value">#{macro}</td>
                  <td className="px-3 py-1.5 fanuc-label">macro</td>
                  <td className="px-3 py-1.5 text-right font-mono fanuc-value tabular-nums">{count}</td>
                </tr>
              ))}
              {Object.entries(pmc).map(([addr, v]) => (
                <tr key={`p-${addr}`} style={{ borderTop: '1px solid color-mix(in srgb, var(--fx-dim) 25%, transparent)' }}>
                  <td className="px-3 py-1.5 font-mono fanuc-value">{addr}</td>
                  <td className="px-3 py-1.5 fanuc-label">pmc</td>
                  <td className="px-3 py-1.5 text-right font-mono fanuc-value tabular-nums">{v?.value ?? '—'}</td>
                </tr>
              ))}
              {!hasSinks && (
                <tr>
                  <td colSpan={3} className="px-3 py-3 text-center fanuc-label">No tool-life sinks resolved</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CrtSection>
    </div>
  );
}
